import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { useDispatch, useSelector } from "react-redux";

import Logo from "../assets/Logo.png";
import { changePassword } from "../actions/auth";
import "./UserAccountPage.css";

const initialState = {
  passwordCurrent: "",
  password: "",
  passwordConfirm: "",
};

function getPasswordStrength(password) {
  let score = 0;

  if (password.length >= 8) score += 25;
  if (/[A-Z]/.test(password)) score += 25;
  if (/[a-z]/.test(password)) score += 20;
  if (/[0-9]/.test(password)) score += 15;
  if (/[^\w\s]/.test(password)) score += 15;

  return Math.min(score, 100);
}

function getStrengthLabel(score) {
  if (score >= 90) return "Strong";
  if (score >= 65) return "Good";
  if (score >= 35) return "Weak";
  return "Very Weak";
}

export default function ChangePassword() {
  const dispatch = useDispatch();

  const authState = useSelector((state) => state.auth || {});

  const [changePasswordData, setChangePasswordData] =
    React.useState(initialState);

  const [error, setError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [showMessage, setShowMessage] = React.useState(false);

  const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const passwordStrength = React.useMemo(
    () => getPasswordStrength(changePasswordData.password),
    [changePasswordData.password],
  );

  const strengthLabel = getStrengthLabel(passwordStrength);

  const updateField = (field, value) => {
    setChangePasswordData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (error) setError("");
  };

  const validateForm = () => {
    const { passwordCurrent, password, passwordConfirm } = changePasswordData;

    if (!passwordCurrent || !password || !passwordConfirm) {
      return "Please fill all required fields";
    }

    if (password !== passwordConfirm) {
      return "Passwords do not match";
    }

    const passwordPattern =
      /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[^\w\s]).{8,}$/;

    if (!passwordPattern.test(password)) {
      return "Password must be at least 8 characters and include uppercase, lowercase, number, and special character.";
    }

    if (passwordCurrent === password) {
      return "New password cannot be the same as old password";
    }

    return "";
  };

  const sendData = async () => {
    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    setShowMessage(true);
    setIsLoading(true);

    try {
      await dispatch(changePassword(changePasswordData));

      setTimeout(() => {
        setIsLoading(false);
        setChangePasswordData(initialState);
      }, 1200);
    } catch (err) {
      setIsLoading(false);
      setError("Something went wrong while changing password");
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 5,
          overflow: "hidden",
          boxShadow: "0 18px 45px rgba(15, 23, 42, .08)",
          border: "1px solid #e5e7eb",
          background: "#fff",
          maxWidth: 820,
          mx: "auto",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            color: "#fff",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Stack direction="row" spacing={1.6} alignItems="center">
              <Box
                sx={{
                  width: 58,
                  height: 58,
                  borderRadius: 4,
                  background: "rgba(255,255,255,.95)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 12px 28px rgba(0,0,0,.18)",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={Logo}
                  alt="Company logo"
                  sx={{
                    width: 46,
                    height: 46,
                    objectFit: "contain",
                  }}
                />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontWeight: 950,
                    fontSize: { xs: 21, md: 25 },
                    lineHeight: 1.1,
                  }}
                >
                  Change Password
                </Typography>

                <Typography
                  sx={{
                    color: "#cbd5e1",
                    fontSize: 13,
                    mt: 0.5,
                  }}
                >
                  Update your account password securely.
                </Typography>
              </Box>
            </Stack>

            <Chip
              icon={<ShieldRoundedIcon />}
              label="Secure Update"
              sx={{
                borderRadius: 999,
                fontWeight: 900,
                color: "#bfdbfe",
                background: "rgba(22, 119, 255, .18)",
                border: "1px solid rgba(147,197,253,.24)",
                "& .MuiChip-icon": {
                  color: "#93c5fd",
                },
              }}
            />
          </Stack>
        </Box>

        {/* Body */}
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          }}
        >
          {showMessage && (
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              {authState.changePasswordError && (
                <Alert
                  severity="error"
                  icon={<ErrorOutlineRoundedIcon />}
                  sx={{
                    borderRadius: 3,
                    fontWeight: 700,
                  }}
                >
                  Error: {authState.changePasswordError}
                </Alert>
              )}

              {authState.changePasswordSuccess && (
                <Alert
                  severity="success"
                  icon={<CheckCircleRoundedIcon />}
                  sx={{
                    borderRadius: 3,
                    fontWeight: 700,
                  }}
                >
                  {authState.changePasswordSuccess}
                </Alert>
              )}
            </Stack>
          )}

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 3,
                background: "#eff6ff",
                color: "#1d4ed8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LockResetRoundedIcon />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontWeight: 950,
                  color: "#0f172a",
                  fontSize: 18,
                  lineHeight: 1.1,
                }}
              >
                Password Details
              </Typography>

              <Typography
                sx={{
                  color: "#64748b",
                  fontSize: 12,
                  mt: 0.3,
                }}
              >
                Use a strong password that you do not use elsewhere.
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ mb: 2.5 }} />

          <Stack spacing={2}>
            <TextField
              required
              fullWidth
              label="Old password"
              type={showCurrentPassword ? "text" : "password"}
              value={changePasswordData.passwordCurrent}
              onChange={(e) => updateField("passwordCurrent", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockRoundedIcon sx={{ color: "#64748b" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showCurrentPassword ? (
                        <VisibilityOffRoundedIcon />
                      ) : (
                        <VisibilityRoundedIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  background: "#fff",
                },
              }}
            />

            <TextField
              required
              fullWidth
              label="New password"
              type={showNewPassword ? "text" : "password"}
              value={changePasswordData.password}
              onChange={(e) => updateField("password", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockRoundedIcon sx={{ color: "#64748b" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showNewPassword ? (
                        <VisibilityOffRoundedIcon />
                      ) : (
                        <VisibilityRoundedIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  background: "#fff",
                },
              }}
            />

            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 0.7 }}
              >
                <Typography
                  sx={{
                    color: "#64748b",
                    fontWeight: 800,
                    fontSize: 12,
                  }}
                >
                  Password strength
                </Typography>

                <Typography
                  sx={{
                    color:
                      passwordStrength >= 90
                        ? "#15803d"
                        : passwordStrength >= 65
                          ? "#1d4ed8"
                          : passwordStrength >= 35
                            ? "#b45309"
                            : "#be123c",
                    fontWeight: 900,
                    fontSize: 12,
                  }}
                >
                  {strengthLabel}
                </Typography>
              </Stack>

              <LinearProgress
                variant="determinate"
                value={passwordStrength}
                sx={{
                  height: 8,
                  borderRadius: 999,
                  background: "#e5e7eb",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 999,
                    background:
                      passwordStrength >= 90
                        ? "#22c55e"
                        : passwordStrength >= 65
                          ? "#1677ff"
                          : passwordStrength >= 35
                            ? "#f59e0b"
                            : "#ef4444",
                  },
                }}
              />
            </Box>

            <TextField
              required
              fullWidth
              label="Confirm new password"
              type={showConfirmPassword ? "text" : "password"}
              value={changePasswordData.passwordConfirm}
              onChange={(e) => updateField("passwordConfirm", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockRoundedIcon sx={{ color: "#64748b" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showConfirmPassword ? (
                        <VisibilityOffRoundedIcon />
                      ) : (
                        <VisibilityRoundedIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  background: "#fff",
                },
              }}
            />

            {error && (
              <Alert
                severity="error"
                icon={<ErrorOutlineRoundedIcon />}
                sx={{
                  borderRadius: 3,
                  fontWeight: 700,
                }}
              >
                {error}
              </Alert>
            )}

            <Box
              sx={{
                p: 2,
                borderRadius: 4,
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 900,
                  color: "#1d4ed8",
                  fontSize: 13,
                  mb: 0.5,
                }}
              >
                Password must contain:
              </Typography>

              <Typography sx={{ color: "#64748b", fontSize: 12 }}>
                At least 8 characters, uppercase letter, lowercase letter,
                number, and special character.
              </Typography>
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="flex-end"
              spacing={1}
              sx={{ pt: 1 }}
            >
              <Button
                variant="outlined"
                disabled={isLoading}
                onClick={() => {
                  setChangePasswordData(initialState);
                  setError("");
                }}
                sx={{
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 900,
                }}
              >
                Clear
              </Button>

              <Button
                onClick={sendData}
                variant="contained"
                startIcon={<SaveRoundedIcon />}
                disabled={isLoading}
                sx={{
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 900,
                  px: 3,
                  background:
                    "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
                  boxShadow: "0 10px 20px rgba(22, 119, 255, .25)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #0958d9 0%, #1677ff 100%)",
                  },
                }}
              >
                {isLoading ? "Changing..." : "Change Password"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}
